/**
 * The equipment slots in their canonical ORDER, and the display names for companion stats.
 *
 * <p>Extracted from `EquipmentSheet.tsx` when the Bag's own `ItemDetailSheet` grew an equipment
 * block of its own (it lists an item's `stats_json.bonuses`). Two hand-maintained copies of
 * "ward → Ward" would have been two places for the same rename to be missed — the exact drift
 * `speciesCatalog.contentDrift.test.ts` exists to catch elsewhere in this codebase.
 *
 * <p>`SLOT_DEFS` mirrors `equipment/EquipmentService`'s own "the 6 functional equipment slots"
 * (see `api/equipment.ts`'s `EquipmentStateDto` doc comment). It is a fixed, ordered list rather
 * than something derived from the DTO's `slots` map, whose key order is a server implementation
 * detail — the equipment sheet's row order must never depend on it. Its `label`s exist only so
 * that list reads as one thing; **turning a slot code into a label is `lib/slotLabel`'s job, for
 * every slot in the game**. A table here that knew only these six is what printed `body_over` at
 * players in the bag sheet.
 */
export const SLOT_DEFS: { code: string; label: string }[] = [
  { code: 'weapon', label: 'Weapon' },
  { code: 'offhand', label: 'Offhand' },
  { code: 'head', label: 'Head' },
  { code: 'body', label: 'Body' },
  { code: 'trinket', label: 'Trinket' },
  { code: 'charm', label: 'Charm' },
];

/** The seven stats, in `StatService.STATS` order. Keyed by the same strings the server uses in
 *  both `CompanionDto.stats` and an equipment item's `stats_json.bonuses`. */
export const STAT_DEFS: { key: string; label: string }[] = [
  { key: 'vitality', label: 'Vitality' },
  { key: 'power', label: 'Power' },
  { key: 'focus', label: 'Focus' },
  { key: 'guard', label: 'Guard' },
  { key: 'ward', label: 'Ward' },
  { key: 'speed', label: 'Speed' },
  { key: 'fortune', label: 'Fortune' },
];

/** Falls back to the raw key rather than hiding an unrecognised bonus: content is data, and a
 *  stat this build has never heard of should still render as *something*. */
export function statLabel(key: string): string {
  return STAT_DEFS.find((s) => s.key === key)?.label ?? key;
}
